import { useEffect, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useLocation, useParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { apiFetch } from '../services/apiUtils';
import { daysOnMarket, providerLabel, safeEnquiryCategory } from './sellerActivityDisplay';
import SellerReportWorkflow from '../components/seller-report/SellerReportWorkflow';
import type { ReportTerminology } from '../components/seller-report/reportTypes';
import { formatSellerReportDate, formatSellerReportMoney, formatSellerReportPeriod, sellerReportWindowCount } from '../components/seller-report/formatters';
import { listingsReturnLinkState } from './listingsReturnState';
import { PortalPerformance, type PortalPerformanceData } from '../components/seller-report/PortalPerformance';

type ActivityTab = 'overview' | 'leads' | 'timeline' | 'seller-report';
type ActivityError = Error & { status?: number };

type SellerActivityOverview = {
  listing: {
    id: string;
    listingReference: string | null;
    saleOrRent: string | null;
    terminology: ReportTerminology | null;
    address: string | null;
    price: string | null;
    listingDate: string | null;
    status: string | null;
    listingStatus: string | null;
    imageUrl: string | null;
    agentName: string | null;
    agentMobile: string | null;
    agentEmail: string | null;
    agentImageUrl: string | null;
    agentMarketCentre: string | null;
    marketCentre: string | null;
    marketCentreDocumentLogoUrl: string | null;
  };
  metrics: { totalEnquiries: number; last7Days: number; last30Days: number; uniqueEnquirersEstimate: number };
  byProvider: Array<{ provider: string; count: number }>;
  trend: Array<{ date: string; count: number }>;
};

export type SellerActivityLead = {
  leadId: string;
  canonicalContactId: string | null;
  contactRelationship: 'CANONICAL' | 'IDENTITY_RESTRICTED' | 'UNMATCHED';
  dateTime: string;
  occurredAt: string | null;
  fullName: string | null;
  phone: string | null;
  email: string | null;
  provider: string;
  enquiryType: string | null;
  status: string;
  message: string | null;
};

type LeadsPage = { rows: SellerActivityLead[]; total: number; limit: number; offset: number };

export type SellerReport = {
  property: Omit<SellerActivityOverview['listing'], 'id'>;
  terminology: ReportTerminology | null;
  period: { from: string; to: string; timezone: string };
  totals: { enquiries: number; uniqueEnquirersEstimate: number };
  providers: Array<{ provider: string; count: number }>;
  trend: Array<{ date: string; count: number }>;
  activities: Array<{ date: string; provider: string; category: string; count: number }>;
  portalPerformance?: PortalPerformanceData;
};

type LeadFilters = { from: string; to: string; provider: string; type: string; status: string; search: string };

const emptyFilters: LeadFilters = { from: '', to: '', provider: '', type: '', status: '', search: '' };
const pageSize = 25;
const tabs: Array<{ id: ActivityTab; label: string }> = [
  { id: 'overview', label: 'Overview' },
  { id: 'leads', label: 'Leads' },
  { id: 'timeline', label: 'Timeline' },
  { id: 'seller-report', label: 'Seller Report' },
];

async function fetchActivity<T>(url: string, token: string, contextId: string): Promise<T> {
  const response = await apiFetch(url, {
    headers: {
      Authorization: `Bearer ${token}`,
      'X-Active-Context': contextId,
    },
  });
  if (!response.ok) {
    const error = new Error(response.status === 403 ? 'You are not authorised to view Seller Activity for this listing.' : 'Seller Activity could not be loaded.') as ActivityError;
    error.status = response.status;
    throw error;
  }
  return response.json() as Promise<T>;
}

function formatMoney(value: string | null): string {
  if (!value) return 'Not set';
  const amount = Number(value);
  return Number.isFinite(amount) ? new Intl.NumberFormat('en-ZA', { style: 'currency', currency: 'ZAR', maximumFractionDigits: 0 }).format(amount) : value;
}

function formatDate(value: string | null): string {
  if (!value) return 'Not set';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? value : new Intl.DateTimeFormat('en-ZA', { dateStyle: 'medium', timeZone: 'Africa/Johannesburg' }).format(date);
}

export function MetadataRow({ label, value, className = '' }: { label: string; value: React.ReactNode; className?: string }) {
  return <dl className={`flex min-w-0 flex-wrap items-baseline self-start gap-x-2 text-sm ${className}`}>
    <dt className="shrink-0 text-slate-600">{label}</dt>
    <dd className="m-0 min-w-0 break-words font-medium tabular-nums text-slate-900">{value}</dd>
  </dl>;
}

function QueryState({ isLoading, error, empty, children }: { isLoading: boolean; error: ActivityError | null; empty?: boolean; children: React.ReactNode }) {
  if (isLoading) return <div className="rounded-lg border border-slate-200 bg-white p-8 text-sm text-slate-500">Loading Seller Activity…</div>;
  if (error?.status === 403) return <div role="alert" className="rounded-lg border border-amber-300 bg-amber-50 p-5 text-sm text-amber-950">You do not have access to this listing’s Seller Activity in the active context.</div>;
  if (error?.status === 404) return <div role="alert" className="rounded-lg border border-slate-300 bg-white p-5 text-sm text-slate-700">This listing could not be found.</div>;
  if (error) return <div role="alert" className="rounded-lg border border-red-200 bg-red-50 p-5 text-sm text-red-800">Seller Activity could not be loaded. Try again.</div>;
  if (empty) return <div className="rounded-lg border border-slate-200 bg-white p-8 text-sm text-slate-500">No activity for this period.</div>;
  return <>{children}</>;
}

export function ActivitySnapshot({ metrics }: { metrics: SellerActivityOverview['metrics'] }) {
  return <section aria-label="Activity Snapshot" className="flex min-h-[280px] min-w-0 flex-col rounded-lg border border-slate-200 bg-white p-6">
    <h2 className="mb-4 border-b border-slate-200 pb-3 text-base font-semibold text-slate-950">Activity Snapshot</h2>
    <dl className="grid flex-1 auto-rows-fr grid-cols-2">
      {[
        ['Total enquiries', metrics.totalEnquiries],
        ['Unique enquirers', metrics.uniqueEnquirersEstimate],
        ['Last 7 days', metrics.last7Days],
        ['Last 30 days', metrics.last30Days],
      ].map(([label, value]) => <div key={String(label)} className="flex min-w-0 flex-col justify-between gap-2 border-slate-200 px-4 py-3 even:border-l [&:nth-child(n+3)]:border-t"><dt className="text-xs font-medium text-slate-500">{label}</dt><dd className="m-0 break-words text-4xl font-bold leading-none tabular-nums text-slate-950">{value}</dd></div>)}
    </dl>
  </section>;
}

function BarRows({ rows }: { rows: Array<{ label: string; count: number }> }) {
  const maximum = Math.max(1, ...rows.map((row) => row.count));
  if (!rows.length) return <p className="text-sm text-slate-500">No activity for this period.</p>;
  return <div className="space-y-3">
    {rows.map((row) => (
      <div key={row.label} className="grid grid-cols-[minmax(0,9rem)_minmax(0,1fr)_auto] items-center gap-3 text-sm">
        <span className="break-words text-slate-700">{row.label}</span>
        <span className="h-2 overflow-hidden rounded bg-slate-100"><span className="block h-full rounded bg-red-700" style={{ width: `${row.count / maximum * 100}%` }} /></span>
        <span className="min-w-6 text-right font-semibold text-slate-900">{row.count}</span>
      </div>
    ))}
  </div>;
}

function PerformanceSection({ listingId, token, contextId, from, to, onFrom, onTo }: {
  listingId: string; token: string; contextId: string; from: string; to: string;
  onFrom: (value: string) => void; onTo: (value: string) => void;
}) {
  const params = new URLSearchParams();
  if (from) params.set('from', from);
  if (to) params.set('to', to);
  const query = useQuery({
    queryKey: ['seller-activity', 'performance', listingId, contextId, from, to],
    queryFn: () => fetchActivity<PortalPerformanceData>(`/api/listings/${encodeURIComponent(listingId)}/seller-activity/performance?${params.toString()}`, token, contextId),
    retry: false,
  });
  return <section aria-label="Portal Performance card" className="min-w-0 rounded-lg border border-slate-200 bg-white p-6">
    <div className="flex flex-wrap items-end gap-4"><label className="text-xs text-slate-600">From<input type="date" value={from} onChange={(event) => onFrom(event.target.value)} className="mt-1 block rounded border border-slate-300 px-3 py-2 text-sm" /></label>
      <label className="text-xs text-slate-600">To<input type="date" value={to} onChange={(event) => onTo(event.target.value)} className="mt-1 block rounded border border-slate-300 px-3 py-2 text-sm" /></label></div>
    {query.data ? <PortalPerformance data={query.data} title="Portal Performance" /> : <p className="mt-4 text-sm text-slate-500">{query.isLoading ? 'Loading performance...' : 'Not available'}</p>}
  </section>;
}

export default function ListingSellerActivity() {
  const { listingId = '' } = useParams();
  const location = useLocation();
  const { token, activeContext } = useAuth();
  const queryClient = useQueryClient();
  const contextId = activeContext?.id ?? '';
  const [tab, setTab] = useState<ActivityTab>('overview');
  const [draftFilters, setDraftFilters] = useState<LeadFilters>(emptyFilters);
  const [filters, setFilters] = useState<LeadFilters>(emptyFilters);
  const [page, setPage] = useState(0);
  const [reportFrom, setReportFrom] = useState('');
  const [reportTo, setReportTo] = useState('');
  const [sellerNotes, setSellerNotes] = useState('');

  useEffect(() => {
    queryClient.removeQueries({
      queryKey: ['seller-activity'],
      predicate: (query) => query.queryKey[2] !== listingId || query.queryKey[3] !== contextId,
    });
    setSellerNotes('');
    setPage(0);
    setDraftFilters(emptyFilters);
    setFilters(emptyFilters);
    setReportFrom('');
    setReportTo('');
  }, [contextId, listingId, queryClient]);

  const enabled = Boolean(token && contextId && listingId);
  const summaryQuery = useQuery({
    queryKey: ['seller-activity', 'summary', listingId, contextId],
    queryFn: () => fetchActivity<SellerActivityOverview>(`/api/listings/${encodeURIComponent(listingId)}/seller-activity/summary`, token!, contextId),
    enabled,
    retry: false,
  });

  const leadsParams = new URLSearchParams({ limit: String(pageSize), offset: String(page * pageSize), sort: tab === 'timeline' ? 'oldest' : 'newest' });
  for (const [key, value] of Object.entries(filters)) if (value) leadsParams.set(key, value);
  const leadsQuery = useQuery({
    queryKey: ['seller-activity', 'leads', listingId, contextId, tab, page, filters],
    queryFn: () => fetchActivity<LeadsPage>(`/api/listings/${encodeURIComponent(listingId)}/seller-activity/leads?${leadsParams.toString()}`, token!, contextId),
    enabled: enabled && (tab === 'leads' || tab === 'timeline'),
    retry: false,
  });

  const reportParams = new URLSearchParams();
  if (reportFrom) reportParams.set('from', reportFrom);
  if (reportTo) reportParams.set('to', reportTo);
  const reportQuery = useQuery({
    queryKey: ['seller-activity', 'seller-report', listingId, contextId, reportFrom, reportTo],
    queryFn: () => fetchActivity<SellerReport>(`/api/listings/${encodeURIComponent(listingId)}/seller-activity/seller-report?${reportParams.toString()}`, token!, contextId),
    placeholderData: (previousData) => previousData,
    enabled: enabled && tab === 'seller-report',
    retry: false,
  });

  const performanceEnabled = import.meta.env.VITE_PORTAL_PERFORMANCE_ENABLED === 'true';

  const overview = summaryQuery.data;
  const leadPage = leadsQuery.data;
  const currentError = tab === 'seller-report'
    ? reportQuery.error as ActivityError | null
    : tab === 'leads' || tab === 'timeline'
      ? leadsQuery.error as ActivityError | null
      : summaryQuery.error as ActivityError | null;
  const isLoading = tab === 'seller-report'
    ? reportQuery.isLoading
    : tab === 'leads' || tab === 'timeline'
      ? leadsQuery.isLoading
      : summaryQuery.isLoading;
  const totalPages = Math.max(1, Math.ceil((leadPage?.total ?? 0) / pageSize));

  const applyFilters = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setFilters({ ...draftFilters });
    setPage(0);
  };
  const clearFilters = () => {
    setDraftFilters(emptyFilters);
    setFilters(emptyFilters);
    setPage(0);
  };

  return (
    <section className="min-w-0 space-y-6" aria-label="Seller Activity">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <Link to="/listings" state={listingsReturnLinkState(location.state)} className="text-sm font-medium text-red-800 hover:underline">← Listings</Link>
          <h1 className="mt-2 text-2xl font-semibold text-slate-950">Seller Activity</h1>
        </div>
        {overview?.listing.listingReference && <span className="rounded border border-slate-200 bg-white px-3 py-1.5 text-sm font-semibold text-slate-800">{overview.listing.listingReference}</span>}
      </div>

      {overview && <div aria-label="Listing summary cards" className="grid w-full min-w-0 grid-cols-1 items-stretch gap-5 lg:grid-cols-[minmax(0,1.85fr)_minmax(0,1fr)]">
        <section aria-label="Property summary" className="flex min-h-[280px] min-w-0 flex-col rounded-lg border border-slate-200 bg-white p-6">
          <h2 className="mb-4 border-b border-slate-200 pb-3 text-base font-semibold text-slate-950">Property Summary</h2>
          <div aria-label="Property summary body" className="flex min-w-0 flex-col gap-6 sm:flex-row sm:items-start">
            {overview.listing.imageUrl
              ? <img src={overview.listing.imageUrl} alt="Listing" className="aspect-[4/3] w-full rounded object-cover sm:w-[200px] sm:shrink-0" />
              : <div className="flex aspect-[4/3] w-full items-center justify-center rounded bg-slate-100 text-sm text-slate-500 sm:w-[200px] sm:shrink-0">No image</div>}
            <div aria-label="Property content" className="w-fit min-w-0 max-w-full space-y-3 sm:max-w-[40rem]">
              <p className="break-words text-xl font-semibold leading-snug text-slate-950">{overview.listing.address || 'Address not available'}</p>
              <div aria-label="Property metadata" className="flex w-fit max-w-full flex-col gap-x-6 gap-y-3 sm:flex-row sm:flex-wrap sm:items-start">
                <div className="min-w-0 max-w-[14rem] space-y-1">
                  <MetadataRow label="Asking price" value={formatMoney(overview.listing.price)} />
                  <MetadataRow label="On market" value={formatDate(overview.listing.listingDate)} />
                  <MetadataRow label="Agent" value={overview.listing.agentName || 'Not set'} />
                </div>
                <div className="min-w-0 max-w-[14rem] space-y-1">
                  <MetadataRow label="Status" value={overview.listing.status || 'Not set'} />
                  <MetadataRow label="Market Centre" value={overview.listing.marketCentre || 'Not set'} />
                </div>
                <div className="min-w-0 max-w-[14rem] space-y-1">
                  <MetadataRow label="Listing Status" value={overview.listing.listingStatus || 'Not set'} />
                  <MetadataRow label="Days On Market" value={daysOnMarket(overview.listing.listingDate) ?? '—'} />
                </div>
              </div>
            </div>
          </div>
        </section>
        <ActivitySnapshot metrics={overview.metrics} />
      </div>}

      <div className="border-b border-slate-200" role="tablist" aria-label="Seller Activity views">
        <div className="flex gap-1 overflow-x-auto">
          {tabs.map((item) => <button key={item.id} type="button" role="tab" aria-selected={tab === item.id} onClick={() => setTab(item.id)} className={`whitespace-nowrap border-b-2 px-4 py-3 text-sm font-medium ${tab === item.id ? 'border-red-700 text-red-800' : 'border-transparent text-slate-600 hover:text-slate-900'}`}>{item.id === 'seller-report' ? overview?.listing.terminology?.reportTitle ?? 'Listing Report' : item.label}</button>)}
        </div>
      </div>

      {!token || !contextId ? <div role="alert" className="rounded-lg border border-amber-300 bg-amber-50 p-5 text-sm text-amber-950">Choose an active role context to view Seller Activity.</div> : (
        <QueryState isLoading={isLoading} error={currentError}>
          {tab === 'overview' && overview && <div className="space-y-5">
            {performanceEnabled && token && <PerformanceSection listingId={listingId} token={token} contextId={contextId}
              from={reportFrom} to={reportTo} onFrom={setReportFrom} onTo={setReportTo} />}
            <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
              {[
                ['Total enquiries', overview.metrics.totalEnquiries],
                ['Last 7 days', overview.metrics.last7Days],
                ['Last 30 days', overview.metrics.last30Days],
                ['Unique enquirers', overview.metrics.uniqueEnquirersEstimate],
              ].map(([label, value]) => <div key={String(label)} className="flex min-h-[116px] flex-col justify-between rounded-lg border border-slate-200 bg-white p-5"><p className="text-sm text-slate-600">{label}</p><p className="mt-3 text-3xl font-semibold tabular-nums text-slate-950">{value}</p></div>)}
            </div>
            <div className="grid gap-8 border-t border-slate-200 pt-6 lg:grid-cols-2">
              <section className="min-w-0"><h2 className="mb-5 font-semibold text-slate-900">Enquiries by day · 90 days</h2><BarRows rows={overview.trend.map((item) => ({ label: formatDate(item.date), count: item.count }))} /></section>
              <section className="min-w-0"><h2 className="mb-5 font-semibold text-slate-900">By provider</h2><BarRows rows={overview.byProvider.map((item) => ({ label: providerLabel(item.provider), count: item.count }))} /></section>
            </div>
          </div>}

          {(tab === 'leads' || tab === 'timeline') && <div className="space-y-4">
            <form onSubmit={applyFilters} className="grid min-w-0 gap-3 border-b border-slate-200 pb-5 sm:grid-cols-2 lg:grid-cols-4 [&_input]:min-w-0 [&_select]:min-w-0">
              <label className="text-sm text-slate-700">From<input type="date" value={draftFilters.from} onChange={(event) => setDraftFilters({ ...draftFilters, from: event.target.value })} className="mt-1 block w-full rounded border border-slate-300 px-2.5 py-2" /></label>
              <label className="text-sm text-slate-700">To<input type="date" value={draftFilters.to} onChange={(event) => setDraftFilters({ ...draftFilters, to: event.target.value })} className="mt-1 block w-full rounded border border-slate-300 px-2.5 py-2" /></label>
              <label className="text-sm text-slate-700">Provider<select value={draftFilters.provider} onChange={(event) => setDraftFilters({ ...draftFilters, provider: event.target.value })} className="mt-1 block w-full rounded border border-slate-300 bg-white px-2.5 py-2"><option value="">All providers</option>{(overview?.byProvider ?? []).map((item) => <option key={item.provider} value={item.provider}>{providerLabel(item.provider)}</option>)}</select></label>
              <label className="text-sm text-slate-700">Enquiry type<input value={draftFilters.type} onChange={(event) => setDraftFilters({ ...draftFilters, type: event.target.value })} className="mt-1 block w-full rounded border border-slate-300 px-2.5 py-2" /></label>
              <label className="text-sm text-slate-700">Status<input value={draftFilters.status} onChange={(event) => setDraftFilters({ ...draftFilters, status: event.target.value })} className="mt-1 block w-full rounded border border-slate-300 px-2.5 py-2" /></label>
              <label className="text-sm text-slate-700 sm:col-span-2">Search<input value={draftFilters.search} onChange={(event) => setDraftFilters({ ...draftFilters, search: event.target.value })} className="mt-1 block w-full rounded border border-slate-300 px-2.5 py-2" placeholder="Name, email, phone or message" /></label>
              <div className="flex items-end gap-3"><button type="submit" className="rounded bg-red-800 px-4 py-2 text-sm font-semibold text-white hover:bg-red-900">Apply</button><button type="button" onClick={clearFilters} className="text-sm font-medium text-slate-700 underline">Clear Filters</button></div>
            </form>
            {!leadPage?.rows.length ? <div role="status" className="border-y border-dashed border-slate-200 py-12 text-center"><h2 className="font-semibold text-slate-800">No matching enquiries.</h2><p className="mt-2 text-sm text-slate-500">{overview?.metrics.totalEnquiries === 0 ? 'No enquiries recorded for this listing yet.' : 'No activity for this period.'}</p></div>
              : tab === 'timeline' ? <EnquiryTimeline leads={leadPage.rows} /> : <InternalLeads leads={leadPage.rows} />}
            <div className="flex flex-wrap items-center justify-between gap-3 border-t border-slate-200 pt-4 text-sm"><span className="text-slate-600">{leadPage?.total ? `${page * pageSize + 1}–${Math.min((page + 1) * pageSize, leadPage.total)} of ${leadPage.total} enquiries` : '0 enquiries'} · page {page + 1} of {totalPages}</span><div className="flex gap-2"><button type="button" onClick={() => setPage(Math.max(0, page - 1))} disabled={page === 0} className="rounded border border-slate-300 bg-white px-3 py-2 disabled:opacity-40">Previous</button><button type="button" onClick={() => setPage(Math.min(totalPages - 1, page + 1))} disabled={page + 1 >= totalPages} className="rounded border border-slate-300 bg-white px-3 py-2 disabled:opacity-40">Next</button></div></div>
          </div>}

          {tab === 'seller-report' && reportQuery.data?.terminology && <SellerReportWorkflow listingId={listingId} token={token} contextId={contextId} terminology={reportQuery.data.terminology}
            from={reportFrom || reportQuery.data.period.from} to={reportTo || reportQuery.data.period.to} notes={sellerNotes}
            onNotesChange={setSellerNotes} onFromChange={setReportFrom} onToChange={setReportTo}>
            <SellerReportPreview report={reportQuery.data} sellerUpdateText={sellerNotes} />
          </SellerReportWorkflow>}
          {tab === 'seller-report' && reportQuery.data && !reportQuery.data.terminology && <div role="alert" className="border-y border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-950">Report terminology is unavailable for this listing type.</div>}
        </QueryState>
      )}
    </section>
  );
}

function activityTimestamp(value: string): Date {
  return new Date(/(?:Z|[+-]\d{2}:?\d{2})$/i.test(value) ? value : `${value.replace(' ', 'T')}+02:00`);
}

function formatDateTime(value: string | null): string {
  if (!value) return 'Date not recorded';
  const date = activityTimestamp(value);
  return Number.isNaN(date.getTime())
    ? value
    : new Intl.DateTimeFormat('en-ZA', { dateStyle: 'medium', timeStyle: 'short', timeZone: 'Africa/Johannesburg' }).format(date);
}

export function BusinessImage({ src, alt, portrait = false, fallback }: {
  src: string | null;
  alt: string;
  portrait?: boolean;
  fallback: React.ReactNode;
}) {
  const [failed, setFailed] = useState(false);
  useEffect(() => setFailed(false), [src]);
  return <div className={portrait ? 'flex h-20 w-20 shrink-0 items-center justify-center overflow-hidden rounded-full bg-slate-100 text-xl font-semibold text-slate-600' : 'flex h-16 w-48 max-w-full items-center'}>
    {src && !failed ? <img src={src} alt={alt} onError={() => setFailed(true)} className={portrait ? 'h-full w-full rounded-full object-cover' : 'max-h-full max-w-full object-contain object-left'} /> : fallback}
  </div>;
}

function agentInitials(name: string | null): string {
  const words = (name ?? '').trim().split(/\s+/).filter(Boolean);
  return words.length ? `${words[0][0]}${words.length > 1 ? words[words.length - 1][0] : ''}`.toUpperCase() : 'KW';
}

export function SellerReportPreview({ report, sellerUpdateText = '' }: {
  report: SellerReport;
  sellerUpdateText?: string;
}) {
  if (!report.terminology) return <section aria-label="Seller report preview"><p role="status">Report terminology is unavailable for this listing type.</p></section>;
  const terminology = report.terminology;
  const metrics: Array<[string, number]> = [
    ['Total enquiries', report.totals.enquiries],
    ['Unique enquirers', report.totals.uniqueEnquirersEstimate],
    ['Last 7 days', sellerReportWindowCount(report.trend, report.period.to, 7)],
    ['Last 30 days', sellerReportWindowCount(report.trend, report.period.to, 30)],
  ];
  return (
      <section aria-label="Seller report preview" className="min-w-0 space-y-7 border border-slate-200 border-t-4 border-t-red-700 bg-white p-5 sm:p-8">
        <header className="flex flex-wrap items-start justify-between gap-5 border-b border-slate-200 pb-6">
          <div className="min-w-0"><BusinessImage src={report.property.marketCentreDocumentLogoUrl} alt={`${report.property.marketCentre || 'Market Centre'} document logo`} fallback={<p className="text-lg font-semibold text-red-800">Keller Williams</p>} /><p className="mt-2 text-xs uppercase text-slate-500">{report.property.marketCentre || 'South Africa'}</p><p className="mt-1 text-xs text-slate-500">Keller Williams · {terminology.updateTitle}</p></div>
          <div className="sm:text-right"><p className="text-xs font-semibold uppercase text-slate-500">{terminology.reportTitle}</p><h2 className="mt-2 text-xl font-semibold text-slate-950">Activity for {report.property.listingReference || 'your property'}</h2><p className="mt-2 text-sm text-slate-600">Reporting period: {formatSellerReportPeriod(report.period.from, report.period.to)}</p></div>
        </header>
        <div aria-label="Property and agent summary" className="grid grid-cols-1 min-w-0 items-start gap-5 lg:grid-cols-[minmax(0,1.65fr)_minmax(0,1fr)]">
          <div aria-label="Report property" className={`grid min-w-0 gap-4 ${report.property.imageUrl ? 'sm:grid-cols-[128px_minmax(0,1fr)]' : ''}`}>
            {report.property.imageUrl && <img src={report.property.imageUrl} alt="Property" className="aspect-[4/3] w-full rounded object-cover" />}
            <div className="min-w-0"><p className="break-words text-lg font-semibold leading-snug text-slate-950">{report.property.address || 'Address not available'}</p><p className="mt-2 text-lg font-semibold text-slate-900">{formatSellerReportMoney(report.property.price)}</p><div className="mt-3 flex flex-wrap gap-x-5 gap-y-2 text-sm text-slate-500"><span>On market {report.property.listingDate ? formatSellerReportDate(report.property.listingDate) : 'Not set'}</span><span>Status {report.property.status || 'Not set'}</span><span>Listing Status {report.property.listingStatus || 'Not set'}</span><span>Days On Market {daysOnMarket(report.property.listingDate) ?? '—'}</span></div></div>
          </div>
          <section aria-label="Listing agent" className="flex min-w-0 items-start gap-4 border-t border-slate-200 pt-5 lg:border-l lg:border-t-0 lg:pl-5 lg:pt-0">
            <BusinessImage src={report.property.agentImageUrl} alt={`${report.property.agentName || 'Listing agent'} profile`} portrait fallback={<span aria-label="Agent photo unavailable">{agentInitials(report.property.agentName)}</span>} />
            <div className="min-w-0 flex-1"><h3 className="break-words text-base font-semibold text-slate-950">{report.property.agentName || 'Listing agent not available'}</h3><p className="mt-1 break-words text-sm text-slate-500">{report.property.agentMarketCentre || 'Market Centre not available'}</p><div className="mt-3 space-y-1 text-sm text-slate-700">{report.property.agentMobile && <p>{report.property.agentMobile}</p>}{report.property.agentEmail && <p className="break-all">{report.property.agentEmail}</p>}</div></div>
          </section>
        </div>
        <section aria-label="Activity Snapshot" className="grid grid-cols-2 gap-px border-y border-slate-200 bg-slate-200">{metrics.map(([label,value])=><div key={label} className="min-w-0 bg-white px-4 py-4 sm:px-5"><p className="text-sm text-slate-600">{label}</p><p className="mt-2 text-3xl font-semibold tabular-nums text-slate-950">{value}</p></div>)}</section>
        {report.portalPerformance && <PortalPerformance data={report.portalPerformance} />}
        <div className="grid gap-8 lg:grid-cols-2"><div className="min-w-0"><h3 className="mb-5 font-semibold text-slate-900">Source breakdown</h3><ProviderBreakdown rows={report.providers} /></div><div className="min-w-0"><h3 className="mb-5 font-semibold text-slate-900">Activity trend</h3><BarRows rows={report.trend.map((item) => ({ label: formatSellerReportDate(item.date), count: item.count }))} /></div></div>
        <div className="border-t border-slate-200 pt-6"><h3 className="mb-5 font-semibold text-slate-900">Enquiry Activity</h3>{report.activities.length ? <ol aria-label="Enquiry Activity" className="ml-2 space-y-5 border-l-2 border-slate-200 pl-5">{[...report.activities].sort((first, second) => first.date.localeCompare(second.date)).map((activity, index) => <li key={`${activity.date}-${activity.provider}-${activity.category}-${index}`} className="relative min-w-0"><span aria-hidden="true" className="absolute -left-[27px] top-1.5 h-3 w-3 rounded-full border-2 border-white bg-red-700" /><time className="text-sm font-semibold text-slate-800">{formatSellerReportDate(activity.date)}</time><div className="mt-2 grid min-w-0 grid-cols-[minmax(0,8rem)_minmax(0,1fr)_auto] items-center gap-2 sm:gap-3"><ProviderBadge provider={activity.provider} /><span className="min-w-0 break-words text-sm text-slate-600">{safeEnquiryCategory(activity.category)}</span><span className="whitespace-nowrap text-right text-sm font-semibold tabular-nums text-slate-900">{activity.count} {activity.count === 1 ? 'enquiry' : 'enquiries'}</span></div></li>)}</ol> : <p className="text-sm text-slate-500">No activity for this period.</p>}</div>
        <section className="border-t border-slate-200 pt-6"><div className="rounded-lg border border-emerald-200 bg-emerald-50/60 px-5 py-4"><h3 className="text-base font-semibold text-emerald-900">{report.portalPerformance ? 'Message from your agent' : terminology.updateTitle}</h3><p className="mt-2 whitespace-pre-wrap break-words text-sm leading-relaxed text-slate-700">{sellerUpdateText || `No ${terminology.audienceNoun.toLowerCase()} update was added for this reporting period.`}</p></div></section>
      </section>
  );
}

function ProviderBadge({ provider }: { provider: string }) {
  const tone = provider === 'PROPERTY24' ? 'bg-cyan-50 text-cyan-900 ring-cyan-200'
    : provider === 'PRIVATE_PROPERTY' ? 'bg-emerald-50 text-emerald-900 ring-emerald-200'
      : provider === 'KWHOMES' ? 'bg-red-50 text-red-900 ring-red-200' : 'bg-slate-50 text-slate-700 ring-slate-200';
  return <span className={`inline-flex max-w-full break-all rounded px-2 py-1 text-xs font-semibold ring-1 ring-inset ${tone}`}>{providerLabel(provider)}</span>;
}

function ProviderBreakdown({ rows }: { rows: Array<{ provider: string; count: number }> }) {
  const maximum = Math.max(1, ...rows.map((row) => row.count));
  return rows.length ? <div className="space-y-3">{rows.map((row) => <div key={row.provider} className="grid min-w-0 grid-cols-[minmax(0,8rem)_minmax(0,1fr)_auto] items-center gap-3"><div className="min-w-0"><ProviderBadge provider={row.provider} /></div><span className="h-2 overflow-hidden rounded bg-slate-100"><span className="block h-full rounded bg-red-700" style={{ width: `${row.count / maximum * 100}%` }} /></span><span className="text-right text-sm font-semibold tabular-nums text-slate-900">{row.count}</span></div>)}</div> : <p className="text-sm text-slate-500">No enquiries recorded during this reporting period.</p>;
}

function ContactDetails({ lead }: { lead: SellerActivityLead }) {
  return <div className="mt-2 space-y-1 break-words text-sm text-slate-600">
    {lead.email && <p className="break-all">{lead.email}</p>}
    {lead.phone && <p>{lead.phone}</p>}
    <p className="text-xs text-slate-500">{lead.contactRelationship === 'IDENTITY_RESTRICTED' ? 'Contact identity restricted by scope' : lead.contactRelationship === 'CANONICAL' ? 'Canonical contact' : 'Unmatched contact'}</p>
  </div>;
}

export function InternalLeads({ leads }: { leads: SellerActivityLead[] }) {
  return <div aria-label="Listing enquiries" role="table" className="min-w-0 border-y border-slate-200 bg-white">
    <div role="row" className="hidden grid-cols-[minmax(0,1.4fr)_minmax(0,1fr)_minmax(0,1fr)_minmax(0,.6fr)] gap-5 border-b border-slate-200 bg-slate-50 px-4 py-3 text-xs font-semibold uppercase text-slate-500 lg:grid">
      {['Buyer / contact', 'Source / enquiry', 'Received', 'Status'].map((label) => <span role="columnheader" key={label}>{label}</span>)}
    </div>
    {leads.map((lead) => <article key={lead.leadId} role="row" className="grid min-w-0 gap-4 border-b border-slate-100 p-4 last:border-b-0 lg:grid-cols-[minmax(0,1.4fr)_minmax(0,1fr)_minmax(0,1fr)_minmax(0,.6fr)]">
      <div role="cell" className="min-w-0"><h3 className="break-words font-semibold text-slate-950">{lead.fullName || 'Name not provided'}</h3><ContactDetails lead={lead} /></div>
      <div role="cell" className="min-w-0"><ProviderBadge provider={lead.provider} /><p className="mt-2 text-sm text-slate-800">{safeEnquiryCategory(lead.enquiryType)}</p><p className="mt-1 break-words text-xs text-slate-500">Type: {lead.enquiryType || 'Not supplied'}</p></div>
      <time role="cell" className="text-sm text-slate-600">{formatDateTime(lead.dateTime)}</time>
      <div role="cell" className="break-words text-xs font-semibold text-slate-700">{lead.status}</div>
      {lead.message && <p role="cell" className="min-w-0 whitespace-pre-wrap break-words border-t border-slate-100 pt-3 text-sm leading-relaxed text-slate-700 lg:col-span-4">{lead.message}</p>}
    </article>)}
  </div>;
}

export function groupEnquiriesByDate(leads: SellerActivityLead[]): Array<{ date: string; leads: SellerActivityLead[] }> {
  const groups = new Map<string, SellerActivityLead[]>();
  for (const lead of [...leads].sort((first, second) => first.dateTime.localeCompare(second.dateTime))) {
    const date = lead.dateTime.slice(0, 10);
    groups.set(date, [...(groups.get(date) ?? []), lead]);
  }
  return [...groups].map(([date, rows]) => ({ date, leads: rows }));
}

export function EnquiryTimeline({ leads }: { leads: SellerActivityLead[] }) {
  return <div aria-label="Enquiry timeline" className="space-y-8 py-2">
    {groupEnquiriesByDate(leads).map((group) => <section key={group.date} aria-label={group.date}>
      <div className="mb-4 flex items-center gap-3"><h2 className="text-sm font-semibold text-slate-900">{formatDate(group.date)}</h2><span className="text-xs text-slate-500">{group.leads.length} {group.leads.length === 1 ? 'enquiry' : 'enquiries'}</span></div>
      <ol className="ml-2 space-y-5 border-l-2 border-slate-200 pl-5 sm:pl-7">
        {group.leads.map((lead) => <li key={lead.leadId} className="relative min-w-0">
          <span aria-hidden="true" className="absolute -left-[27px] top-1.5 h-3 w-3 rounded-full border-2 border-white bg-red-700 sm:-left-[35px]" />
          <article className="min-w-0 border-b border-slate-200 pb-5">
            <div className="flex flex-wrap items-center gap-3"><time className="text-sm font-semibold tabular-nums text-slate-700">{Number.isNaN(activityTimestamp(lead.dateTime).getTime()) ? 'Time not recorded' : new Intl.DateTimeFormat('en-ZA', { timeStyle: 'short', timeZone: 'Africa/Johannesburg' }).format(activityTimestamp(lead.dateTime))}</time><ProviderBadge provider={lead.provider} /><span className="text-xs font-medium text-slate-500">{lead.status}</span></div>
            <h3 className="mt-3 font-semibold text-slate-950">{safeEnquiryCategory(lead.enquiryType)}</h3>
            <p className="mt-1 break-words text-sm font-medium text-slate-800">{lead.fullName || 'Name not provided'}</p>
            <ContactDetails lead={lead} />
            <p className="mt-2 break-words text-xs text-slate-500">Type: {lead.enquiryType || 'Not supplied'}</p>
            {lead.message && <p className="mt-3 whitespace-pre-wrap break-words text-sm leading-relaxed text-slate-700">{lead.message}</p>}
          </article>
        </li>)}
      </ol>
    </section>)}
  </div>;
}